import { useQuery } from "@tanstack/react-query";
import { TrendUpIcon, TrendDownIcon, ScalesIcon, SpinnerGapIcon } from "@phosphor-icons/react";
import { fetchHolderFlow } from "../lib/api";

const CFG = {
  accumulation: { label: "Accumulation", color: "#00E599", icon: TrendUpIcon, blurb: "Buyers are stepping in harder than sellers — the top-holder buy cluster that tends to mark a run's start." },
  distribution: { label: "Distribution", color: "#FF3366", icon: TrendDownIcon, blurb: "Sellers are offloading into strength — the peak-sell exit pattern. Be careful chasing up here." },
  neutral: { label: "Balanced", color: "#9aa4b2", icon: ScalesIcon, blurb: "Buys and sells are roughly matched right now — no clear accumulation or distribution." },
};

const Stat = ({ label, value, sub, color }) => (
  <div className="border border-white/10 bg-black/30 p-2">
    <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-wide">{label}</div>
    <div className="font-mono font-bold text-sm" style={{ color }}>{value}</div>
    <div className="text-[10px] font-mono text-muted-foreground/70">{sub}</div>
  </div>
);

export const HolderFlow = ({ address }) => {
  const { data, isLoading } = useQuery({ queryKey: ["holderFlow", address], queryFn: () => fetchHolderFlow(address), staleTime: 300000, enabled: !!address });
  if (isLoading) return (
    <div className="border border-white/10 p-4 flex items-center gap-2 text-xs text-muted-foreground font-mono" data-testid="holder-flow-loading">
      <SpinnerGapIcon size={16} className="animate-spin text-neon" /> Reading recent on-chain flow…
    </div>
  );
  if (!data?.available) return null;
  const c = CFG[data.class] || CFG.neutral;
  const Icon = c.icon;
  return (
    <div data-testid="holder-flow" className="border p-4 sm:p-5" style={{ borderColor: `${c.color}66`, background: `${c.color}0d` }}>
      <div className="flex items-center gap-2.5 mb-2">
        <Icon size={22} weight="bold" style={{ color: c.color }} />
        <div className="font-heading font-bold text-lg">Holder Flow: <span style={{ color: c.color }}>{c.label}</span></div>
        <span className="text-[10px] font-mono text-muted-foreground ml-auto">last {data.trades} trades</span>
      </div>
      <p className="text-xs text-muted-foreground mb-3 max-w-xl">{c.blurb}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
        <Stat label="Buys" value={data.buys} sub={`${data.buySol} SOL`} color="#00E599" />
        <Stat label="Sells" value={data.sells} sub={`${data.sellSol} SOL`} color="#FF3366" />
        <Stat label="Net" value={`${data.netSol >= 0 ? "+" : ""}${data.netSol} SOL`} sub="buy − sell" color={data.netSol >= 0 ? "#00E599" : "#FF3366"} />
        <Stat label="Traders" value={`${data.uniqueBuyers}/${data.uniqueSellers}`} sub="buyers / sellers" color="#9aa4b2" />
      </div>
      <p className="text-[10px] text-muted-foreground/60 mt-3">Recent trades only (Helius) — a momentum snapshot, not full holder history. Not financial advice.</p>
      {typeof data.botScore === "number" && (
        <div className="mt-4 pt-3 border-t border-white/10" data-testid="bot-activity">
          <div className="flex items-center gap-2">
            <span className="text-sm">🤖</span>
            <span className="font-heading font-bold text-sm">Bot Activity:</span>
            <span className="font-mono font-bold text-sm" style={{ color: data.botLevel === "high" ? "#FF3366" : data.botLevel === "medium" ? "#f59e0b" : "#00E599" }}>
              {data.botLevel === "high" ? "High" : data.botLevel === "medium" ? "Medium" : "Low"} ({data.botScore}/100)
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">
            {data.botLevel === "high"
              ? "⚠️ Looks wash-traded — few wallets cycling / uniform sizes. Fake volume is a classic pump-and-dump setup."
              : data.botLevel === "medium"
              ? "Some bot-like churn. Real interest is mixed with automated trades — take the volume with a pinch of salt."
              : "Trades look mostly organic — many distinct wallets, little round-tripping."}
          </p>
          <div className="text-[10px] font-mono text-muted-foreground/70 mt-1.5">
            {data.uniqueTraders} distinct wallets across {data.trades} trades · {data.roundtripWallets} round-tripped (bought & sold)
          </div>
        </div>
      )}
    </div>
  );
};
